/* External */
import {
  memo, useCallback, useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';

/* Utils */
import backendService from '~/services/backendService';
import { redirectTo } from '~/utils/history';
import { Routes, getLink } from '~/utils/router';
import { NEW_TERMS_API } from '~/utils/env';
import { useIsForegroundApp } from '../ForegroundAppProvider';
import { useUserAccount } from '../UserProvider';
import useRequestReducer from '../../hooks/useRequestReducer';
import { TermsOfServiceContext } from './contexts';
import {
  getTermsOfService,
  mergePrevAndNewTerms,
  shouldUserAcceptNewTerms,
} from './utils';

function TermsOfServiceProvider({ children }) {
  const {
    state: {
      isLoading,
      response: termsOfService,
    },
    performRequest,
  } = useRequestReducer(getTermsOfService, mergePrevAndNewTerms);
  const { iso2Code } = useUserAccount() || {};
  const isForegroundApp = useIsForegroundApp();

  const [currentPath, setCurrentPath] = useState(window.location.href);

  const {
    state: {
      isLoading: isLoadingShouldAccept,
      response: shouldAccept,
    },
    performRequest: performShouldUserAcceptNewTerms,
  } = useRequestReducer(shouldUserAcceptNewTerms, (oldRes, newRes) => newRes);

  const acceptNewTerms = useCallback(async () => {
    try {
      await backendService.customer.v1.acknowledge({
        accepted: {
          termsAndConditions: true,
          privacyPolicy: true,
        },
      });
      await performShouldUserAcceptNewTerms(iso2Code?.toLowerCase());
    } catch (error) {
      console.error('TermsOfServiceProvider>acceptNewTerms', error);
      throw error;
    }
  }, [iso2Code, performShouldUserAcceptNewTerms]);

  useEffect(() => {
    if (iso2Code && isForegroundApp) {
      performRequest(iso2Code?.toLowerCase());
    }
  }, [isForegroundApp, iso2Code, performRequest]);

  useEffect(() => {
    if (
      NEW_TERMS_API
      && !isLoading
      && termsOfService
      && isForegroundApp
      && iso2Code
    ) {
      setCurrentPath(window.location.href);
      performShouldUserAcceptNewTerms(iso2Code.toLowerCase());
    }
  }, [
    isLoading,
    termsOfService,
    performShouldUserAcceptNewTerms,
    isForegroundApp,
    iso2Code,
  ]);

  useEffect(() => {
    if (!isLoadingShouldAccept && termsOfService && isForegroundApp && shouldAccept) {
      redirectTo(getLink(Routes.Private.AcceptTermsOfService), {}, true);
    }
  }, [isLoadingShouldAccept, termsOfService, isForegroundApp, shouldAccept]);

  const contextValue = useMemo(() => ({
    currentPath,
    termsOfService,
    isLoading,
    shouldAccept,
    isLoadingShouldAccept,
    setCurrentPath,
    acceptNewTerms,
    performShouldUserAcceptNewTerms,
  }), [
    currentPath,
    isLoading,
    shouldAccept,
    termsOfService,
    isLoadingShouldAccept,
    setCurrentPath,
    acceptNewTerms,
    performShouldUserAcceptNewTerms,
  ]);

  return (
    <TermsOfServiceContext.Provider value={contextValue}>
      {children}
    </TermsOfServiceContext.Provider>
  );
}

TermsOfServiceProvider.propTypes = {
  children: PropTypes.node,
};

TermsOfServiceProvider.defaultProps = {
  children: undefined,
};

export default memo(TermsOfServiceProvider);
